import { useEffect, useMemo, useState } from 'react'

/**
 * A single statement transaction row, independent of any storage identity.
 * Shared by the authenticated results view (StatementDetailsPage) and the public
 * demo (DemoConverter) so the two presentations can never drift.
 */
export type TransactionRow = {
  transaction_date: string | null
  description: string | null
  debit: number | null
  credit: number | null
  balance: number | null
}

type SortKey = 'transaction_date' | 'description' | 'debit' | 'credit' | 'balance'

/** Presentational, self-contained transactions table: sorting + pagination, no data fetching. */
export default function TransactionsTable({ transactions }: { transactions: TransactionRow[] }) {
  const [sortKey, setSortKey] = useState<SortKey>('transaction_date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Reset to the first page whenever a new dataset arrives (e.g. switching/reloading statements),
  // matching the prior StatementDetailsPage behaviour.
  useEffect(() => setPage(1), [transactions])

  const sorted = useMemo(() => {
    const arr = [...transactions]
    arr.sort((a, b) => {
      const valA = a[sortKey]
      const valB = b[sortKey]
      let cmp = 0
      if (sortKey === 'debit' || sortKey === 'credit' || sortKey === 'balance') {
        const na = typeof valA === 'number' ? (valA as number) : Number.NEGATIVE_INFINITY
        const nb = typeof valB === 'number' ? (valB as number) : Number.NEGATIVE_INFINITY
        cmp = na === nb ? 0 : na < nb ? -1 : 1
      } else {
        const sa = (valA || '') as string
        const sb = (valB || '') as string
        cmp = sa.localeCompare(sb)
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [transactions, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return sorted.slice(start, start + pageSize)
  }, [sorted, safePage, pageSize])

  const setSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '')

  if (transactions.length === 0) {
    return <div className="text-center py-12 text-gray-500">No transactions found.</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="text-gray-600">
            <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('transaction_date')}>Date {arrow('transaction_date')}</th>
            <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('description')}>Description {arrow('description')}</th>
            <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('debit')}>Debit {arrow('debit')}</th>
            <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('credit')}>Credit {arrow('credit')}</th>
            <th className="py-2 px-3 cursor-pointer" onClick={() => setSort('balance')}>Balance {arrow('balance')}</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((t, i) => (
            <tr key={`${t.transaction_date ?? ''}-${t.description ?? ''}-${(safePage - 1) * pageSize + i}`} className="border-t">
              <td className="py-2 px-3 whitespace-nowrap">{t.transaction_date || '-'}</td>
              <td className="py-2 px-3">{t.description || '-'}</td>
              <td className="py-2 px-3">{t.debit ?? '-'}</td>
              <td className="py-2 px-3">{t.credit ?? '-'}</td>
              <td className="py-2 px-3">{t.balance ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          Page {safePage} of {totalPages} · Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, sorted.length)} of {sorted.length}
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">Rows:</label>
          <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1) }} className="border rounded px-2 py-1 text-sm">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className={`px-3 py-1 rounded ${safePage <= 1 ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}>Prev</button>
          <button disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className={`px-3 py-1 rounded ${safePage >= totalPages ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}>Next</button>
        </div>
      </div>
    </div>
  )
}
